import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { BodyLarge, BodySmall, Button, H1, H6 } from '@atlassian/mpac-primitives';
import { css } from '@compiled/react';
import DOMPurify from 'dompurify';
import React from 'react';

import type { BundleConfig } from '../config/bundleConfig';

const heroContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: token('space.150'),
  marginBottom: token('space.200'),
  marginTop: token('space.300'),
  [media.above.md]: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
});

const heroContentStyles = css({
  width: '100%',
  [media.above.md]: {
    width: 'auto',
    flex: '1',
  },
});

const desktopHeroImageStyles = css({
  display: 'none',
  [media.above.md]: {
    display: 'flex',
    justifyContent: 'center',
    width: '40%',
  },
});

const heroAppsContainerStyles = css({
  display: 'flex',
  width: '100%',
  marginTop: token('space.400'),
  marginBottom: token('space.100'),
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  gap: 0,
});

const heroAppCardStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  width: '100%',
  marginBottom: token('space.500'),
  borderRadius: '20px',
  boxShadow: `0px 0px 1px 0px rgba(30, 31, 33, 0.31), 0px 1px 1px 0px rgba(30, 31, 33, 0.25)`,
  cursor: 'pointer',
  padding: token('space.250'),
  transition: 'box-shadow 0.2s ease-in-out',
  '&:hover': {
    boxShadow: token('elevation.shadow.overlay'),
    textDecoration: 'none',
  },
  '&:active': {
    background: token('elevation.surface.raised.hovered'),
    boxShadow: token('elevation.shadow.overlay'),
    textDecoration: 'none',
  },
  '&:focus-visible': {
    outline: `${token('space.025')} solid ${token('color.border.focused')}`,
    boxShadow: token('elevation.shadow.overlay'),
    textDecoration: 'none',
  },
});

const heroAppIconContainerStyles = css({
  width: '3.125rem',
  height: '3.125rem',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  marginRight: token('space.150'),
  borderRadius: '8px',
  border: `1px solid ${token('color.border')}`,
  padding: '9px',
});

const heroAppContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const heroAppTitleStyles = css({
  margin: 0,
  overflow: 'hidden',
  display: '-webkit-box',
  '-webkit-line-clamp': '2',
  '-webkit-box-orient': 'vertical',
  'text-overflow': 'ellipsis',
});

const heroAppDescriptionStyles = css({
  margin: 0,
});

const heroTitleStyles = css({
  fontWeight: '700',
  marginTop: 0,
});

const bundleHeaderImageStyles = css({
  marginBottom: token('space.100'),
});

const linkStyles = css({
  width: '100%',
  textDecoration: 'none',
  [media.above.sm]: {
    width: '48%',
  },
  '&:hover': {
    textDecoration: 'none',
  },
  '&:active': {
    textDecoration: 'none',
  },
  '&:focus-visible': {
    textDecoration: 'none',
  },
});

interface HeroAppCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  url: string;
}

interface HeroSectionProps {
  bundleConfig: BundleConfig;
  onRequestClick: () => void;
}

const HeroAppCard = ({ title, description, icon, url }: HeroAppCardProps) => {
  return (
    <a target="_blank" rel="noopener noreferrer" href={url} css={linkStyles}>
      <div css={heroAppCardStyles}>
        <div css={heroAppIconContainerStyles}>{icon}</div>
        <div css={heroAppContentStyles}>
          <H6 css={heroAppTitleStyles}>{title}</H6>
          <BodySmall css={heroAppDescriptionStyles}>{description}</BodySmall>
        </div>
      </div>
    </a>
  );
};

export const HeroSection: React.FC<HeroSectionProps> = ({ bundleConfig, onRequestClick }) => {
  return (
    <div
      css={[
        heroContainerStyles,
        css({
          marginBottom: bundleConfig.apps.length > 2 ? token('space.100') : token('space.200'),
        }),
      ]}
    >
      <div css={heroContentStyles}>
        <img
          src={bundleConfig.hero.headerImage}
          alt={bundleConfig.hero.headerImageAlt}
          css={bundleHeaderImageStyles}
        />
        <H1 css={heroTitleStyles}>{bundleConfig.hero.title}</H1>
        <BodyLarge
          // eslint-disable-next-line marketplace-frontend/no-dangerous-html
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bundleConfig.hero.description) }}
        />
        <div css={heroAppsContainerStyles}>
          {bundleConfig.apps.map((app, index) => (
            <HeroAppCard
              key={index}
              icon={<img src={app.icon} alt={app.iconAlt} width="32px" height="32px" />}
              title={app.title}
              description={app.description}
              url={app.url}
            />
          ))}
        </div>
        {bundleConfig.hero.requestButton && (
          <Button appearance="primary" onClick={onRequestClick}>
            {bundleConfig.hero.requestButton}
          </Button>
        )}
      </div>
      <div css={desktopHeroImageStyles}>
        <img
          src={bundleConfig.hero.heroImage}
          alt={bundleConfig.hero.heroImageAlt}
          style={{ height: '480px' }}
        />
      </div>
    </div>
  );
};
